import React, { useState, useEffect } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  MenuItem,
  Box,
  Typography,
  Avatar,
  Chip,
  IconButton,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import { customAPI, customGetAPI } from "services/utils/CustomAPI";
import { useSelector } from "react-redux";
import { toast } from "react-toastify";

export default function LogActivityDialog({
  open,
  onClose,
  contact,
  preselectedType,
  onSaved,
}) {
  const { domain } = useSelector((state) => state.tokenReducer);
  const [activityTypes, setActivityTypes] = useState([]);
  const [selectedType, setSelectedType] = useState(null);
  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [date, setDate] = useState(new Date().toISOString().slice(0, 16));
  const [outcome, setOutcome] = useState("");
  const [priority, setPriority] = useState("medium");
  const [saving, setSaving] = useState(false);
  const [errors, setErrors] = useState({});

  useEffect(() => {
    if (!domain) return;
    customGetAPI(`${domain}crm/activity-type/`)
      .then((res) => {
        setActivityTypes(res.responseDetails?.results || []);
      })
      .catch(() => {});
  }, [domain]);

  useEffect(() => {
    if (!open) return;
    const resolved =
      preselectedType && activityTypes.length > 0
        ? activityTypes.find(
            (t) =>
              t.value === preselectedType.value ||
              t.label?.toLowerCase() === preselectedType.label?.toLowerCase()
          ) || null
        : null;
    setSelectedType(resolved);
    setTitle("");
    setDescription("");
    setDate(new Date().toISOString().slice(0, 16));
    setOutcome("");
    setPriority("medium");
    setErrors({});
  }, [open, preselectedType, activityTypes]);

  const handleSave = async () => {
    const errs = {};
    if (!selectedType) errs.type = true;
    if (!title.trim()) errs.title = "Title is required";
    if (Object.keys(errs).length) {
      setErrors(errs);
      return;
    }

    setSaving(true);
    try {
      const payload = {
        contact: contact.id,
        type: selectedType.id,
        title: title.trim(),
        description: description.trim(),
        date: new Date(date).toISOString(),
        completed: new Date(date) <= new Date(),
      };
      if (selectedType.value === "call" && outcome) payload.outcome = outcome;
      if (selectedType.value === "reminder" && priority)
        payload.priority = priority;
      await customAPI(`${domain}crm/activities/`, "POST", payload);
      onSaved();
      onClose();
      toast.success("Activity logged.");
    } catch {
      toast.error("Failed to save activity. Please try again.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{ sx: { borderRadius: 2 } }}
    >
      <DialogTitle
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
        }}
      >
        Log Activity
        <IconButton onClick={onClose}>
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent dividers>
        {contact && (
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 1.5,
              mb: 2.5,
              p: 1.5,
              background: "#F5F5F5",
              borderRadius: 1,
            }}
          >
            <Avatar sx={{ width: 32, height: 32, background: "#1D9E75", fontSize: 13 }}>
              {contact.first_name?.[0]}
              {contact.last_name?.[0]}
            </Avatar>
            <Box>
              <Typography variant="body2" fontWeight={500}>
                {contact.first_name} {contact.last_name}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                {contact.company}
              </Typography>
            </Box>
          </Box>
        )}

        <Typography
          variant="caption"
          sx={{
            fontWeight: 500,
            textTransform: "uppercase",
            letterSpacing: 0.05,
            color: "text.secondary",
            mb: 0.75,
            display: "block",
          }}
        >
          Activity type *
        </Typography>
        <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1, mb: 2.5 }}>
          {activityTypes.map((t) => (
            <Chip
              key={t.id}
              label={t.label}
              onClick={() => setSelectedType(t)}
              variant={selectedType?.id === t.id ? "filled" : "outlined"}
              sx={
                selectedType?.id === t.id
                  ? { background: "#1D9E75", color: "#fff", borderColor: "#1D9E75" }
                  : { borderColor: "#CCC" }
              }
            />
          ))}
        </Box>
        {errors.type && (
          <Typography
            variant="caption"
            color="error"
            sx={{ mt: -2, mb: 1.5, display: "block" }}
          >
            Activity type is required
          </Typography>
        )}

        <TextField
          fullWidth
          label="Title"
          required
          value={title}
          onChange={(e) => setTitle(e.target.value)}
          error={!!errors.title}
          helperText={errors.title}
          sx={{ mb: 2 }}
        />

        <TextField
          fullWidth
          label="Date & time"
          required
          type="datetime-local"
          value={date}
          onChange={(e) => setDate(e.target.value)}
          InputLabelProps={{ shrink: true }}
          sx={{ mb: 2 }}
        />

        <TextField
          fullWidth
          label="Description"
          multiline
          rows={3}
          value={description}
          onChange={(e) => setDescription(e.target.value)}
          sx={{ mb: 2 }}
        />

        {selectedType?.value === "call" && (
          <TextField
            select
            fullWidth
            label="Outcome"
            value={outcome}
            onChange={(e) => setOutcome(e.target.value)}
            sx={{ mb: 2 }}
          >
            {["Answered", "No answer", "Busy", "Left voicemail", "Wrong number"].map((o) => (
              <MenuItem key={o} value={o}>
                {o}
              </MenuItem>
            ))}
          </TextField>
        )}

        {selectedType?.value === "reminder" && (
          <TextField
            select
            fullWidth
            label="Priority"
            value={priority}
            onChange={(e) => setPriority(e.target.value)}
            sx={{ mb: 2 }}
          >
            {["low", "medium", "high", "urgent"].map((p) => (
              <MenuItem key={p} value={p} sx={{ textTransform: "capitalize" }}>
                {p.charAt(0).toUpperCase() + p.slice(1)}
              </MenuItem>
            ))}
          </TextField>
        )}
      </DialogContent>

      <DialogActions sx={{ px: 3, py: 2 }}>
        <Button onClick={onClose} disabled={saving}>
          Cancel
        </Button>
        <Button
          variant="contained"
          onClick={handleSave}
          disabled={saving}
          sx={{ background: "#1D9E75", "&:hover": { background: "#0F6E56" } }}
        >
          {saving ? "Saving…" : "Save Activity"}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
